import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PackageVersion } from '@weave/schema-gen-ts/dist/schemas/integrations/sync-config-api/v1/message.pb';
import { buildVersionsByPackage, getPackageVersionKey, listPackageNames } from '../helpers';
import type { VersionsByPackage } from '../types';

export type DraftTrainRow = { id: string } & PackageVersion;
export type DraftTrainRowOptions = { packages: string[]; versions: string[] };

type DraftTrainRowState = {
  canAddMore: boolean;
  hasArtifacts: boolean;
  getRowOptions: (row: DraftTrainRow) => DraftTrainRowOptions;
};

const EMPTY_OPTIONS: string[] = [];

const createRow = (): DraftTrainRow => ({ id: crypto.randomUUID(), packageName: '', versionTag: '' });

export const getDraftTrainRowState = (
  rows: DraftTrainRow[],
  versionsByPackage: VersionsByPackage,
  allPackages: string[]
): DraftTrainRowState => {
  const versionsUsedByPackage = new Map<string, Set<string>>();
  let totalPairs = 0;

  for (const versions of versionsByPackage.values()) totalPairs += versions.size;

  for (const row of rows) {
    if (!row.packageName || !row.versionTag) continue;

    const usedVersions = versionsUsedByPackage.get(row.packageName) ?? new Set<string>();
    usedVersions.add(row.versionTag);
    versionsUsedByPackage.set(row.packageName, usedVersions);
  }

  const availableVersionsByPackage = new Map<string, string[]>();
  const availablePackages: string[] = [];
  const availablePackageSet = new Set<string>();

  for (const packageName of allPackages) {
    const usedVersions = versionsUsedByPackage.get(packageName);
    const availableVersions = Array.from(versionsByPackage.get(packageName) ?? []).filter(
      (version) => !usedVersions?.has(version)
    );

    availableVersionsByPackage.set(packageName, availableVersions);
    if (availableVersions.length > 0) {
      availablePackages.push(packageName);
      availablePackageSet.add(packageName);
    }
  }

  const rowOptionsById = new Map<string, DraftTrainRowOptions>();
  const packageOptionsByName = new Map<string, string[]>();
  const versionOptionsByPair = new Map<string, string[]>();

  for (const row of rows) {
    const packageName = row.packageName;
    const versionTag = row.versionTag;
    const packageVersions = packageName ? versionsByPackage.get(packageName) : undefined;
    const usedVersions = packageName ? versionsUsedByPackage.get(packageName) : undefined;

    let packages = availablePackages;
    if (packageName && packageVersions && !availablePackageSet.has(packageName)) {
      packages = packageOptionsByName.get(packageName) ?? [packageName, ...availablePackages].sort();
      packageOptionsByName.set(packageName, packages);
    }

    let versions = packageName ? availableVersionsByPackage.get(packageName) ?? EMPTY_OPTIONS : EMPTY_OPTIONS;
    if (packageName && versionTag && packageVersions?.has(versionTag) && usedVersions?.has(versionTag)) {
      const pairKey = getPackageVersionKey(packageName, versionTag);
      versions =
        versionOptionsByPair.get(pairKey) ??
        Array.from(packageVersions).filter((version) => version === versionTag || !usedVersions.has(version));
      versionOptionsByPair.set(pairKey, versions);
    }

    rowOptionsById.set(row.id, { packages, versions });
  }

  const getRowOptions = (row: DraftTrainRow): DraftTrainRowOptions => {
    return rowOptionsById.get(row.id) ?? { packages: availablePackages, versions: EMPTY_OPTIONS };
  };

  return {
    canAddMore: rows.length < totalPairs,
    hasArtifacts: allPackages.length > 0,
    getRowOptions,
  };
};

export const useDraftTrainRows = (artifacts: PackageVersion[], isModalOpen: boolean) => {
  const [rows, setRows] = useState<DraftTrainRow[]>(() => [createRow()]);

  useEffect(() => {
    if (!isModalOpen) setRows([createRow()]);
  }, [isModalOpen]);

  const versionsByPackage = useMemo(() => buildVersionsByPackage(artifacts), [artifacts]);
  const allPackages = useMemo(() => listPackageNames(versionsByPackage), [versionsByPackage]);
  const rowState = useMemo(
    () => getDraftTrainRowState(rows, versionsByPackage, allPackages),
    [allPackages, rows, versionsByPackage]
  );

  const updatePackage = useCallback((id: string, packageName: string) => {
    setRows((currentRows) => currentRows.map((row) => (row.id === id ? { ...row, packageName, versionTag: '' } : row)));
  }, []);

  const updateVersion = useCallback((id: string, versionTag: string) => {
    setRows((currentRows) => currentRows.map((row) => (row.id === id ? { ...row, versionTag } : row)));
  }, []);

  const removeRow = useCallback((id: string) => {
    setRows((currentRows) => currentRows.filter((row) => row.id !== id));
  }, []);

  const addRow = useCallback(() => setRows((currentRows) => [...currentRows, createRow()]), []);

  return { rows, ...rowState, addRow, removeRow, updatePackage, updateVersion };
};
